import React, { useEffect, useState } from "react";
import { AxiosResponse } from "axios";
import { Link } from "react-router-dom";
import NumberFormat from "react-number-format";
import { getRequest } from "../../utils/httpRequest";
import { fetchNextFunFact } from "../../utils/funFacts";

interface PopularCourse {
  name: string;
  slug: string;
  learners: number;
  image_url: string | null;
  alt_image_url: string | null;
}

interface MostActive {
  name: string;
  avatar: string;
  xp: number;
}

interface InsightsPayload {
  popular_course: PopularCourse | null;
  most_active: MostActive | null;
}

interface Props {
  slug: string;
  fact: string;
  userIsInUk?: boolean;
}

// A 30-minute slot with us, bookable any time.
const BOOKING_URL = "https://calendar.app.google/ydaaCJyKYs1Ehx5a6";

// Matches the tile treatment everywhere else: the UK alternate headshot wins
// where we have one, and a course with no artwork still gets the decal rather
// than a hole in the panel.
const courseImage = (course: PopularCourse, userIsInUk?: boolean) => {
  if (userIsInUk && course.alt_image_url) return course.alt_image_url;
  if (course.image_url) return course.image_url;

  return require("../../../../assets/images/courses/decal.png");
};

// Learners are shown by protected name ("Mathias M."), so initials are the
// only fallback available when there's no avatar on file.
const monogram = (name: string) =>
  (name || "")
    .replace(/[^\w\s]/g, "")
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0])
    .join("")
    .toUpperCase();

const TeamInsights = ({ slug, fact, userIsInUk }: Props) => {
  const [insights, setInsights] = useState<InsightsPayload | null>(null);
  const [currentFact, setCurrentFact] = useState(fact);
  const [loadingFact, setLoadingFact] = useState(false);

  useEffect(() => setCurrentFact(fact), [fact]);

  useEffect(() => {
    getRequest(`/api/teams/${slug}/insights.json`)
      .then((response: AxiosResponse<InsightsPayload>) =>
        setInsights(response.data),
      )
      .catch(() => setInsights({ popular_course: null, most_active: null }));
  }, [slug]);

  const anotherFact = () => {
    setLoadingFact(true);

    fetchNextFunFact(currentFact)
      .then((nextFact) => {
        setCurrentFact(nextFact);
        setLoadingFact(false);
      })
      .catch(() => setLoadingFact(false));
  };

  return (
    <section className="month" aria-labelledby="monthTitle">
      {/* Centred over left-aligned content only works if something separates
          the two, so the hairline is load-bearing rather than decoration. */}
      <h2 className="month-title" id="monthTitle">
        Your team this month
      </h2>

      {/* The icon chips are gone. A boxed book and a boxed lightning bolt
          carried nothing the label didn't already say, and their differing
          weights made the left edge look ragged. All three blocks now share
          one left rail and one rhythm: label, value, supporting line. */}
      <div className="mb">
        <h3 className="mb-k">Most popular course</h3>
        {insights?.popular_course ? (
          <>
            {/* Opens in a new tab -- an admin halfway through managing a team
                shouldn't lose their place to a sales page. */}
            <Link
              className="mb-link"
              to={`/courses/${insights.popular_course.slug}`}
              target="_blank"
            >
              <span className="mb-media" aria-hidden="true">
                <span className="mb-thumb">
                  <img
                    src={courseImage(insights.popular_course, userIsInUk)}
                    alt=""
                    width="56"
                    height="32"
                    loading="lazy"
                  />
                </span>
              </span>
              <span className="mb-nm">{insights.popular_course.name}</span>
              <svg className="mb-go" viewBox="0 0 24 24" aria-hidden="true">
                <path d="M5 12h14M13 6l6 6-6 6" />
              </svg>
            </Link>
            <p className="mb-meta">
              {insights.popular_course.learners}{" "}
              {insights.popular_course.learners === 1
                ? "learner enrolled"
                : "learners enrolled"}
            </p>
          </>
        ) : (
          <p className="mb-meta mb-meta--flush">
            Nobody has enrolled on a course yet.
          </p>
        )}
      </div>

      <div className="mb">
        <h3 className="mb-k">Most active learner</h3>
        {insights?.most_active ? (
          <>
            {/* Deliberately not a link: there is nowhere useful to send
                anyone, and a clickable name that goes nowhere is worse than a
                plain one. Keeps the link row's geometry so the name still
                lines up with the course above it. */}
            <div className="mb-row">
              <span className="mb-media" aria-hidden="true">
                <span className="mb-av">
                  {insights.most_active.avatar ? (
                    <img src={insights.most_active.avatar} alt="" />
                  ) : (
                    monogram(insights.most_active.name)
                  )}
                </span>
              </span>
              <span className="mb-nm">{insights.most_active.name}</span>
            </div>
            <p className="mb-meta">
              <NumberFormat
                value={insights.most_active.xp}
                displayType="text"
                thousandSeparator
              />{" "}
              XP in the last 30 days
            </p>
          </>
        ) : (
          <p className="mb-meta mb-meta--flush">
            No XP earned in the last 30 days. A nudge might help.
          </p>
        )}
      </div>

      {currentFact && (
        <div className="mb">
          {/* The refresh sits on the label line, so it never competes with the
              fact text -- and a long fact can't shunt it off the panel. */}
          <div className="mb-fact-head">
            <h3 className="mb-k">Fact of the day</h3>
            <button
              className="mb-another"
              type="button"
              onClick={anotherFact}
              disabled={loadingFact}
            >
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M21 12a9 9 0 1 1-2.6-6.4M21 3v6h-6" />
              </svg>
              {loadingFact ? "Finding one\u2026" : "Another"}
            </button>
          </div>
          <p className="mb-fact" aria-live="polite">
            {currentFact}
          </p>
        </div>
      )}

      {/* The one genuine call to action, so it looks like a button. It used to
          be a white pill with a chevron, indistinguishable from the course row
          above it. */}
      <div className="month-cta">
        <a
          className="btn btn-primary btn--sm btn--block"
          href={BOOKING_URL}
          target="_blank"
          rel="noopener noreferrer"
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M4 5h16v16H4zM4 10h16M9 3v4M15 3v4" />
          </svg>
          Book 30 minutes with us
        </a>
      </div>
    </section>
  );
};

export default TeamInsights;
